import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import CommitRoundedIcon from '@mui/icons-material/CommitRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SNAPSHOT from './gitlabSnapshot';
import { fmt } from './translations';
import { EASE, Intro } from './ui';
import { RESEARCH_INDEX } from './studies';
import styles from './ScreenInspector.module.css';

const HOST = 'https://gitlab.meetyuwen.com';
const PATH = 'testing/vlm-page-recognition';
export const PROJECT_URL = `${HOST}/${PATH}`;
export const RELEASES_URL = `${PROJECT_URL}/-/releases`;
const API = `${HOST}/api/v4/projects/${encodeURIComponent(PATH)}`;

// Kept for the whole visit, so coming back to the slide does not refetch.
let cache = null;

/** Project, latest commit, top-level files and latest release; snapshot until (or unless) the API answers. */
function useGitLab() {
    const [state, setState] = useState(() => cache || { source: 'loading', data: SNAPSHOT });
    useEffect(() => {
        if (cache) return undefined;
        let alive = true;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 8000);
        const get = (p) => fetch(API + p, { signal: ctrl.signal }).then((r) => {
            if (!r.ok) throw new Error(`GitLab ${r.status}`);
            return r.json();
        });
        Promise.all([
            get(''),
            get('/repository/commits?per_page=1'),
            get('/repository/tree?per_page=100'),
            get('/releases?per_page=1'),
        ])
            .then(([project, commits, tree, releases]) => {
                cache = { source: 'live', data: { project, commit: commits[0] || null, tree, release: releases[0] || null } };
                if (alive) setState(cache);
            })
            .catch(() => {
                if (alive) setState({ source: 'snapshot', data: SNAPSHOT });
            })
            .finally(() => clearTimeout(timer));
        return () => {
            alive = false;
            clearTimeout(timer);
            ctrl.abort();
        };
    }, []);
    return state;
}

function day(iso, lang) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Only http(s) links, and GitLab's relative ones resolved against the project.
function safeUrl(url) {
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/uploads/')) return PROJECT_URL + url;
    if (url.startsWith('/')) return HOST + url;
    return null;
}

const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)\s]+\))/g;

function inline(text) {
    const out = [];
    let last = 0;
    let m;
    INLINE.lastIndex = 0;
    while ((m = INLINE.exec(text)) !== null) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const tok = m[0];
        const key = `${m.index}`;
        if (m[1]) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
        else if (m[2]) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
        else {
            const [, label, href] = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
            const url = safeUrl(href);
            out.push(url ? <a key={key} href={url} target="_blank" rel="noreferrer">{label}</a> : label);
        }
        last = m.index + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** The small part of Markdown that release notes use; builds elements, never injects HTML. */
function Markdown({ text }) {
    const blocks = [];
    const lines = (text || '').split('\n');
    let list = null;
    let para = null;
    const flush = () => {
        if (list) blocks.push(<ul key={blocks.length}>{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>);
        if (para) blocks.push(<p key={blocks.length}>{inline(para.join(' '))}</p>);
        list = null;
        para = null;
    };
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.startsWith('```')) {
            flush();
            const code = [];
            for (i += 1; i < lines.length && !lines[i].startsWith('```'); i += 1) code.push(lines[i]);
            blocks.push(<pre key={blocks.length}>{code.join('\n')}</pre>);
        } else if (/^#{1,6}\s/.test(line)) {
            flush();
            blocks.push(<h4 key={blocks.length}>{inline(line.replace(/^#{1,6}\s+/, ''))}</h4>);
        } else if (/^[-*]\s/.test(line)) {
            if (para) flush();
            list = list || [];
            list.push(line.replace(/^[-*]\s+/, ''));
        } else if (!line.trim()) {
            flush();
        } else {
            if (list) flush();
            para = para || [];
            para.push(line.trim());
        }
    }
    flush();
    return <div className={styles.markdown}>{blocks}</div>;
}

function Window({ url, children, foot, delay }) {
    return (
        <motion.div
            className={styles.window}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay, ease: EASE }}
        >
            <div className={styles.windowBar}>
                <span className={styles.windowDots}><i /><i /><i /></span>
                <a className={styles.windowUrl} href={url} target="_blank" rel="noreferrer">
                    {url.replace(/^https:\/\//, '')}
                </a>
            </div>
            <div className={styles.windowBody}>{children}</div>
            <div className={styles.windowFoot}>{foot}</div>
        </motion.div>
    );
}

// Only says something when the saved snapshot is shown instead of live data.
function Status({ source, c }) {
    if (source !== 'snapshot') return null;
    return (
        <span className={styles.windowStatus}>
            <i className={styles.dotIdle} />
            {fmt(c.snapshot, { date: SNAPSHOT.takenAt })}
        </span>
    );
}

function OutLink({ href, children }) {
    return (
        <a className={styles.windowAction} href={href} target="_blank" rel="noreferrer">
            {children}
            <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
        </a>
    );
}

/** Last slide: the GitLab project and its latest release, each in a browser-like window. */
export default function GitLab({ t, lang }) {
    const c = t.code;
    const { source, data } = useGitLab();
    const { project, commit, tree, release } = data;
    const branch = project.default_branch || 'main';
    const sorted = [...(tree || [])].sort((a, b) => (a.type === b.type ? 0 : a.type === 'tree' ? -1 : 1));

    return (
        <>
            <Intro eyebrow={c.eyebrow} title={c.title} sub={c.sub} />

            <div className={styles.windows}>
                <Window
                    url={PROJECT_URL}
                    delay={0.1}
                    foot={<><Status source={source} c={c} /><OutLink href={PROJECT_URL}>{c.openProject}</OutLink></>}
                >
                    <div className={styles.glHead}>
                        <span className={styles.glAvatar}>V</span>
                        <div>
                            <p className={styles.glName}>{project.name}</p>
                            <p className={styles.glPath}>testing / {project.name}</p>
                        </div>
                    </div>
                    <p className={styles.glDesc}>{project.description || c.description}</p>
                    <p className={styles.glStats}>
                        <span><StarBorderRoundedIcon sx={{ fontSize: 15 }} />{project.star_count} {c.stars}</span>
                        <span><CallSplitRoundedIcon sx={{ fontSize: 15 }} />{project.forks_count} {c.forks}</span>
                        <span><code>{branch}</code></span>
                        <span>{c.updated} {day(project.last_activity_at, lang)}</span>
                    </p>
                    {commit && (
                        <a className={styles.glCommit} href={`${PROJECT_URL}/-/commit/${commit.short_id}`} target="_blank" rel="noreferrer">
                            <CommitRoundedIcon sx={{ fontSize: 16 }} />
                            <code>{commit.short_id}</code>
                            <span className={styles.glCommitTitle}>{commit.title}</span>
                            <span className={styles.glCommitDate}>{day(commit.committed_date, lang)}</span>
                        </a>
                    )}
                    <ul className={styles.glTree}>
                        {sorted.map((e) => (
                            <li key={e.path}>
                                <a href={`${PROJECT_URL}/-/${e.type === 'tree' ? 'tree' : 'blob'}/${branch}/${e.path}`} target="_blank" rel="noreferrer">
                                    {e.type === 'tree'
                                        ? <FolderRoundedIcon sx={{ fontSize: 16, color: '#7a8a99' }} />
                                        : <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: '#98a2b3' }} />}
                                    {e.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </Window>

                <Window
                    url={RELEASES_URL}
                    delay={0.22}
                    foot={<><Status source={source} c={c} /><OutLink href={RELEASES_URL}>{c.allReleases}</OutLink></>}
                >
                    {release ? (
                        <>
                            <div className={styles.relHead}>
                                <a className={styles.relTag} href={`${RELEASES_URL}/${release.tag_name}`} target="_blank" rel="noreferrer">
                                    <LocalOfferOutlinedIcon sx={{ fontSize: 15 }} />
                                    {release.tag_name}
                                </a>
                                <span className={styles.relLatest}>{c.latest}</span>
                            </div>
                            <p className={styles.relName}>{release.name}</p>
                            <p className={styles.relMeta}>
                                {release.author && release.author.name} · {day(release.released_at, lang)}
                                {release.commit && <> · <code>{release.commit.short_id}</code></>}
                            </p>
                            <div className={styles.relAssets}>
                                <span>{c.sourceCode}</span>
                                {((release.assets && release.assets.sources) || [])
                                    .filter((s) => ['zip', 'tar.gz'].includes(s.format) && safeUrl(s.url))
                                    .map((s) => <a key={s.format} href={safeUrl(s.url)} target="_blank" rel="noreferrer">{s.format}</a>)}
                            </div>
                            <Markdown text={release.description} />
                        </>
                    ) : (
                        <p className={styles.glDesc}>{c.noRelease}</p>
                    )}
                </Window>
            </div>

            <div className={styles.footer}>
                <p>{t.footer.built}</p>
                <p>
                    {t.footer.note}{' '}
                    <a className={styles.studyLink} href={RESEARCH_INDEX} target="_blank" rel="noreferrer">{t.reliability.index}</a>
                </p>
            </div>
        </>
    );
}
